<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="68px"
    >
      <el-form-item label="套餐名称" prop="name">
        <el-input
          v-model="queryParams.name"
          class="!w-240px"
          clearable
          placeholder="请输入套餐名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select
          v-model="queryParams.status"
          class="!w-240px"
          clearable
          placeholder="请选择状态"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['promotion:package-activity:create']"
          plain
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <el-table-column label="编号" prop="id" min-width="70" />
      <el-table-column label="套餐名称" prop="name" min-width="140" />
      <el-table-column label="主图" min-width="80">
        <template #default="{ row }">
          <el-image
            v-if="row.picUrl"
            :src="row.picUrl"
            :preview-src-list="[row.picUrl]"
            class="h-40px w-40px"
            preview-teleported
          />
        </template>
      </el-table-column>
      <el-table-column
        :formatter="fenToYuanFormat"
        label="一口价"
        min-width="100"
        prop="packagePrice"
      />
      <el-table-column label="已售/总量" min-width="110">
        <template #default="{ row }">
          {{ row.soldCount ?? 0 }} / {{ row.totalCount ?? '-' }}
        </template>
      </el-table-column>
      <el-table-column label="每人限购" min-width="90" prop="userLimitCount" />
      <el-table-column label="活动时间" min-width="220">
        <template #default="{ row }">
          {{ formatDate(row.startTime) }} ~ {{ formatDate(row.endTime) }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="状态" prop="status" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.status"
            :active-value="0"
            :inactive-value="1"
            inline-prompt
            active-text="开"
            inactive-text="关"
            v-hasPermi="['promotion:package-activity:close']"
            @change="(val) => handleStatusChange(row, val)"
          />
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
      />
      <el-table-column align="center" fixed="right" label="操作" width="120">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['promotion:package-activity:update']"
            link
            type="primary"
            @click="openForm('update', row.id)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['promotion:package-activity:delete']"
            link
            type="danger"
            @click="handleDelete(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <PackageActivityForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter, formatDate } from '@/utils/formatTime'
import { fenToYuanFormat } from '@/utils/formatter'
import * as PackageActivityApi from '@/api/mall/promotion/package/packageActivity'
import PackageActivityForm from './PackageActivityForm.vue'
import { CommonStatusEnum } from '@/utils/constants'

defineOptions({ name: 'PromotionPackageActivity' })

const message = useMessage()
const { t } = useI18n()

const loading = ref(true)
const total = ref(0)
const list = ref<any[]>([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: undefined,
  status: undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await PackageActivityApi.getPackageActivityPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value.resetFields()
  handleQuery()
}

const formRef = ref<InstanceType<typeof PackageActivityForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

const handleStatusChange = async (row: any, nextStatus: number) => {
  const enabling = Number(nextStatus) === CommonStatusEnum.ENABLE
  const text = enabling ? '开启' : '关闭'
  const prevStatus = enabling ? CommonStatusEnum.DISABLE : CommonStatusEnum.ENABLE
  try {
    await message.confirm(`确认${text}该套餐？`)
    if (enabling) {
      await PackageActivityApi.openPackageActivity(row.id)
    } else {
      await PackageActivityApi.closePackageActivity(row.id)
    }
    row.status = Number(nextStatus)
    message.success(t('common.success'))
  } catch {
    row.status = prevStatus
  }
}

const handleDelete = async (id: number) => {
  try {
    await message.delConfirm()
    await PackageActivityApi.deletePackageActivity(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

onMounted(() => {
  getList()
})
</script>
